import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  createSlot as createSlotApi,
  deleteSlot as deleteSlotApi,
  getGameSlotDetailsId,
  getGameSlots,
  getWinnersByGameSlotId,
  getWinningNumbers,
} from "services/apiGameSlots";

export function useGameSlots(size) {
  const [currentPage, setCurrentPage] = useState(0);

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: ["gameSlots", currentPage],
    queryFn: async () => {
      return await getGameSlots(currentPage, size);
    },
    keepPreviousData: true,
  });

  const gameSlots = data?.content || [];
  const totalPages = data?.totalPages || 1;
  const totalElements = data?.totalElements || 0;

  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;

  const goToNextPage = () => {
    if (hasNextPage) setCurrentPage((prev) => prev + 1);
  };

  const goToPreviousPage = () => {
    if (hasPreviousPage) setCurrentPage((prev) => prev - 1);
  };

  return {
    gameSlots,
    error,
    isLoading,
    isFetching,
    hasNextPage,
    hasPreviousPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
    currentPage,
    totalElements,
  };
}

export function useLotteryWinningNumbers(gameSlotId) {
  const {
    isLoading,
    data: lotteryWinningNumbers,
    error,
  } = useQuery({
    queryKey: ["LotteryWinningNumbers", gameSlotId],
    queryFn: () => getWinningNumbers(gameSlotId),
  });

  return { isLoading, error, lotteryWinningNumbers };
}

export function useSlotDetails(gameSlotId) {
  const {
    isLoading,
    data: details,
    error,
  } = useQuery({
    queryKey: ["slotDetails", gameSlotId],
    queryFn: () => getGameSlotDetailsId(gameSlotId),
  });

  return { isLoading, error, details };
}
export function useSlotWinners(gameSlotId, size, search) {
  const [currentPage, setCurrentPage] = useState(0);
  const {
    isLoading,
    data,
    error,
    isFetching,
    refetch: refetchWinners,
  } = useQuery({
    queryKey: ["slotWinners", gameSlotId, currentPage, search],
    queryFn: async () => {
      return await getWinnersByGameSlotId(gameSlotId, currentPage, size, search);
    },
    keepPreviousData: true,
  });

  const winners = data?.winnersPage?.content || [];
  const totalPages = data?.winnersPage?.totalPages || 1;

  const hasNextPage = currentPage < totalPages - 1;
  const hasPreviousPage = currentPage > 0;

  const goToNextPage = () => {
    if (hasNextPage) setCurrentPage((prev) => prev + 1);
  };

  const goToPreviousPage = () => {
    if (hasPreviousPage) setCurrentPage((prev) => prev - 1);
  };

  return {
    isLoading,
    error,
    winners,
    isFetching,
    refetchWinners,
    hasNextPage,
    hasPreviousPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
    currentPage,
  };
}

export function useCreateSlot() {
  const queryClient = useQueryClient();

  const { mutate: createSlot, isLoading: isCreating } = useMutation({
    mutationFn: createSlotApi,
    onSuccess: () => {
      toast.success("New slot successfully created");
      queryClient.invalidateQueries({ queryKey: ["gameSlots"] });
    },
    onError: (err) => toast.error(err.message),
  });

  return { isCreating, createSlot };
}

export function useDeleteSlot() {
  const queryClient = useQueryClient();
  const { isLoading: isDeleting, mutate: deleteSlot } = useMutation({
    mutationFn: deleteSlotApi,
    onSuccess: () => {
      toast.success("Slot successfully deleted");

      queryClient.invalidateQueries({
        queryKey: ["gameSlots"],
      });
    },
    onError: (err) => toast.error(err.message),
  });

  return { isDeleting, deleteSlot };
}
